import React, { useEffect } from 'react';
import { captureException } from '@sentry/react';
import { collection, onSnapshot, query, where } from '@firebase/firestore';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { db } from '../../../../firebase-config.js';

type UseGetIntakeStatusBaseStepsParams = {
  patientId: string;
  enabled?: boolean;
};

export const useGetIntakeStatusBaseSteps = ({ patientId, enabled = true }: UseGetIntakeStatusBaseStepsParams) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<IntakeStatusBaseSteps | null>(null);
  const [error, setError] = React.useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const snapshotQuery = query(
      collection(db, 'patients', patientId, 'intakeStatus'),
      where('stepType', '==', 'BASE_STEP'),
    );

    const unsubscribe = onSnapshot(
      snapshotQuery,
      (snapshot) => {
        const intakeStatus = snapshot.docs.reduce<IntakeStatusBaseSteps>((acc, doc) => {
          const data = doc.data() as IntakeStatusBaseSteps[keyof IntakeStatusBaseSteps];
          const id = doc.id as keyof IntakeStatusBaseSteps;

          return {
            ...acc,
            [id]: {
              ...acc[id],
              ...data,
              id,
            },
          };
        }, {} as IntakeStatusBaseSteps);

        setData(intakeStatus);
        setIsLoading(false);
        setError(null);
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.error('Error fetching patient intake status:', error);
        }

        captureException(error, {
          tags: { issueIn: 'useGetIntakeStatusBaseSteps' },
          extra: { patientId },
        });

        setError(error);
        setIsLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [enabled, patientId]);

  return { data, isLoading, error };
};
